/**
 * VolumeReviewCycle — drives one volume through Pink → Red → Gold → White.
 *
 * Companion to VolumeReviewPanel (which schedules reviews and records
 * findings). This one runs the ladder: freeze the volume, hand reviewers an
 * immutable Word file, track recovery, and gate the next colour on findings
 * being closed out.
 *
 * Styled dark to match ProposalWorkspaceLive, which is where it mounts.
 *
 * Findings jump to their section rather than sitting in a list — `onJumpToSection`
 * is what makes a finding actionable instead of a note you have to go hunting for.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Loader2, Snowflake, FileDown, ShieldCheck, ShieldAlert, CornerUpRight,
  CheckCircle2, Circle, Lock, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_META = {
  PINK:  { label: 'Pink Team',  bar: 'bg-pink-400',   text: 'text-pink-300',   note: 'First full draft' },
  RED:   { label: 'Red Team',   bar: 'bg-red-500',    text: 'text-red-300',    note: 'Compliance + win themes' },
  GOLD:  { label: 'Gold Team',  bar: 'bg-amber-400',  text: 'text-amber-300',  note: 'Executive review' },
  WHITE: { label: 'White Team', bar: 'bg-slate-300',  text: 'text-slate-200',  note: 'Final compliance pass' },
};

const STATUS_CHIP = {
  SCHEDULED:   'bg-slate-700 text-slate-300',
  IN_PROGRESS: 'bg-amber-500/20 text-amber-300',
  RECOVERY:    'bg-red-500/20 text-red-300',
  COMPLETE:    'bg-emerald-500/20 text-emerald-300',
  CANCELLED:   'bg-slate-800 text-slate-500',
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

export default function VolumeReviewCycle({ pursuitId, volumeId, volumeLabel, onJumpToSection }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);          // stage id currently transitioning
  const [ladder, setLadder] = useState([]);
  const [versions, setVersions] = useState([]);
  const [reviews, setReviews] = useState({});      // review_id -> full review (for findings)
  const [freezeTarget, setFreezeTarget] = useState(null);   // stage row awaiting confirmation
  const [overrideReason, setOverrideReason] = useState('');

  const base = `${API_URL}/api/pursuits/${pursuitId}`;

  const load = useCallback(async () => {
    if (!volumeId) return;
    setLoading(true);
    try {
      const [statusRes, versionsRes, reviewsRes] = await Promise.all([
        fetch(`${base}/review-cycle/volumes/${volumeId}/status`, { headers: getAuthHeaders() }),
        fetch(`${base}/proposal-versions/volumes/${volumeId}`, { headers: getAuthHeaders() }),
        fetch(`${base}/strategy/color-reviews?volume_id=${volumeId}`, { headers: getAuthHeaders() }),
      ]);
      if (statusRes.ok)   setLadder((await statusRes.json()).stages || []);
      if (versionsRes.ok) setVersions((await versionsRes.json()).versions || []);
      if (reviewsRes.ok) {
        const rows = (await reviewsRes.json()).reviews || [];
        setReviews(Object.fromEntries(rows.map((r) => [r.id, r])));
      }
    } catch {
      toast.error('Could not load the review cycle');
    } finally {
      setLoading(false);
    }
  }, [base, volumeId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const call = async (url, options, okMsg) => {
    const res = await fetch(url, { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, ...options });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // The backend explains *why* a gate is shut — surface that verbatim
      // rather than a generic failure, since the reason is the actionable part.
      throw new Error(data.detail || 'Request failed');
    }
    if (okMsg) toast.success(data.message || okMsg, { duration: 6000 });
    return data;
  };

  const openReview = async (row, reason) => {
    setBusy(row.stage);
    try {
      const data = await call(
        `${base}/review-cycle/reviews/${row.review_id}/open`,
        { method: 'POST', body: JSON.stringify(reason ? { override_reason: reason } : {}) },
        `${STAGE_META[row.stage].label} opened — volume frozen`,
      );
      if (data.warning) toast.warning(data.warning, { duration: 9000 });
      if (data.gate_overridden) toast.warning('Gate overridden — recorded on the review.', { duration: 8000 });
      setFreezeTarget(null); setOverrideReason('');
      await load();
    } catch (e) {
      toast.error(e.message, { duration: 9000 });
    } finally { setBusy(null); }
  };

  const closeReview = async (row) => {
    setBusy(row.stage);
    try {
      await call(`${base}/review-cycle/reviews/${row.review_id}/close-review`, { method: 'POST' });
      await load();
    } catch (e) { toast.error(e.message, { duration: 9000 }); }
    finally { setBusy(null); }
  };

  const completeReview = async (row) => {
    setBusy(row.stage);
    try {
      await call(`${base}/review-cycle/reviews/${row.review_id}/complete`,
        { method: 'POST', body: JSON.stringify({}) });
      await load();
    } catch (e) { toast.error(e.message, { duration: 10000 }); }
    finally { setBusy(null); }
  };

  const toggleFinding = async (reviewId, findingId, resolved) => {
    try {
      await call(`${base}/review-cycle/reviews/${reviewId}/findings/${findingId}`,
        { method: 'PATCH', body: JSON.stringify({ resolved }) });
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const downloadDocx = async (version) => {
    try {
      const res = await fetch(`${base}/proposal-versions/${version.id}/docx`, { headers: getAuthHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'No Word file for this version');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${volumeLabel || 'Volume'} - ${version.label}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message, { duration: 8000 }); }
  };

  const verify = async (version) => {
    try {
      const res = await fetch(`${base}/proposal-versions/${version.id}/verify`, { headers: getAuthHeaders() });
      const d = await res.json();
      if (d.intact) toast.success(`${version.label} is intact — hash matches what was frozen.`);
      else toast.error(`${version.label} FAILED integrity check. This version was altered outside the app.`, { duration: 15000 });
    } catch { toast.error('Could not verify this version'); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading review cycle…
    </div>;
  }

  const activeRow = ladder.find((r) => r.status === 'RECOVERY') || ladder.find((r) => r.status === 'IN_PROGRESS');
  const activeReview = activeRow ? reviews[activeRow.review_id] : null;
  const findings = activeReview?.findings || [];

  return (
    <div className="space-y-5">

      {/* ── Ladder ─────────────────────────────────────────────── */}
      <div className="space-y-2">
        {ladder.map((row) => {
          const meta = STAGE_META[row.stage] || {};
          const isBusy = busy === row.stage;
          const locked = !!row.blocked_by && !row.status;
          return (
            <div key={row.stage}
                 className={`rounded-md border border-slate-700 bg-slate-800/60 p-3 ${locked ? 'opacity-50' : ''}`}
                 style={{ borderLeftWidth: 3 }}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${meta.bar}`} />
                <span className="font-semibold text-sm text-slate-100">{meta.label}</span>
                {row.status && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_CHIP[row.status] || ''}`}>
                    {row.status.replace('_', ' ')}
                  </span>
                )}
                {row.open_findings > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 font-semibold">
                    {row.open_findings} open
                  </span>
                )}
                {locked && <Lock className="w-3 h-3 text-slate-500 ml-auto" />}
              </div>

              <p className="text-[11px] text-slate-400 mt-1">
                {meta.note}
                {row.blocked_by && !row.status && <> · blocked by {STAGE_META[row.blocked_by]?.label}</>}
                {row.version_label && <> · frozen as <span className="text-slate-300">{row.version_label}</span></>}
              </p>

              {row.gate_override_reason && (
                <p className="text-[11px] text-amber-300/90 mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-none" />
                  Gate overridden: {row.gate_override_reason}
                </p>
              )}

              {/* one action per state — never more than one thing to decide */}
              <div className="mt-2">
                {!row.review_id && (
                  <p className="text-[11px] text-slate-500">No review scheduled. Add one on the Schedule tab.</p>
                )}
                {row.status === 'SCHEDULED' && (
                  <Button size="sm" disabled={isBusy}
                          onClick={() => setFreezeTarget(row)}
                          className="w-full bg-slate-100 text-slate-900 hover:bg-white text-xs h-8">
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Snowflake className="w-3 h-3 mr-1" />}
                    Freeze &amp; open {meta.label}
                  </Button>
                )}
                {row.status === 'IN_PROGRESS' && (
                  <Button size="sm" variant="outline" disabled={isBusy}
                          onClick={() => closeReview(row)}
                          className="w-full border-slate-600 text-slate-200 hover:bg-slate-700 text-xs h-8">
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CornerUpRight className="w-3 h-3 mr-1" />}
                    Close review · start recovery
                  </Button>
                )}
                {row.status === 'RECOVERY' && (
                  <Button size="sm" disabled={isBusy || row.open_findings > 0}
                          onClick={() => completeReview(row)}
                          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8">
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" />
                      : row.open_findings > 0
                        ? `${row.open_findings} finding${row.open_findings > 1 ? 's' : ''} open`
                        : <><CheckCircle2 className="w-3 h-3 mr-1" />Complete recovery</>}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Findings in recovery — anchored, not just listed ────── */}
      {activeRow?.status === 'RECOVERY' && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
            {STAGE_META[activeRow.stage]?.label} findings
            {activeRow.open_findings > 0 && <span className="text-red-300"> · {activeRow.open_findings} open</span>}
          </h4>
          {findings.length === 0 && (
            <p className="text-xs text-slate-500">No findings were recorded — recovery can be completed.</p>
          )}
          <div className="space-y-2">
            {findings.map((f) => (
              <div key={f.id}
                   className={`rounded-md border border-slate-700 bg-slate-800/60 p-2.5 ${f.resolved ? 'opacity-55' : ''}`}>
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => toggleFinding(activeRow.review_id, f.id, !f.resolved)}
                    title={f.resolved ? 'Reopen this finding' : 'Mark resolved'}
                    className="mt-0.5 text-slate-400 hover:text-emerald-300">
                    {f.resolved ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => onJumpToSection?.(f.section_id)}
                      className="text-xs font-semibold text-slate-100 hover:text-white hover:underline text-left">
                      {f.section_heading || f.section_id}
                    </button>
                    <p className="text-[11px] text-slate-400 mt-0.5">{f.comment}</p>
                    {f.resolved && f.resolution_note && (
                      <p className="text-[10px] text-emerald-300/80 mt-1">Resolved: {f.resolution_note}</p>
                    )}
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-semibold flex-none">
                    {f.verdict}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Frozen versions ────────────────────────────────────── */}
      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Frozen versions {versions.length > 0 && <span className="text-slate-500">· {versions.length}</span>}
        </h4>
        {versions.length === 0 ? (
          <p className="text-xs text-slate-500">None yet. Opening a review freezes the volume.</p>
        ) : (
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="rounded-md border border-slate-700 bg-slate-800/60 p-2.5">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-sm ${STAGE_META[v.stage]?.bar || 'bg-slate-500'}`} />
                  <span className="text-xs font-semibold text-slate-100">{v.label}</span>
                  <span className="text-[10px] text-slate-500 ml-auto">{fmt(v.frozen_at)}</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  {v.section_count} sections · {v.word_count} words
                  {v.empty_section_count > 0 && <span className="text-amber-300/90"> · {v.empty_section_count} empty</span>}
                  {v.template_filename ? <> · {v.template_filename}</> : <> · no template</>}
                </p>
                <p className="text-[10px] text-slate-500 font-mono mt-0.5">sha256 {String(v.sha256 || '').slice(0, 16)}</p>
                {v.render_error && (
                  <p className="text-[10px] text-red-300/90 mt-1">Word render failed — content is still intact.</p>
                )}
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="outline" disabled={!v.rendered}
                          onClick={() => downloadDocx(v)}
                          className="border-slate-600 text-slate-200 hover:bg-slate-700 text-[11px] h-7 px-2">
                    <FileDown className="w-3 h-3 mr-1" />.docx
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => verify(v)}
                          className="border-slate-600 text-slate-200 hover:bg-slate-700 text-[11px] h-7 px-2">
                    <ShieldCheck className="w-3 h-3 mr-1" />Verify
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Freeze confirmation ────────────────────────────────── */}
      <Dialog open={!!freezeTarget} onOpenChange={(o) => { if (!o) { setFreezeTarget(null); setOverrideReason(''); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Snowflake className="w-4 h-4 text-sky-300" />
              Open {STAGE_META[freezeTarget?.stage]?.label}?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This freezes <strong className="text-slate-200">{volumeLabel}</strong> exactly as it stands and renders it
              through the bound template. The frozen copy can't be edited afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md bg-slate-800 border border-slate-700 p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-400">Version label</span>
              <span className="text-slate-200">{STAGE_META[freezeTarget?.stage]?.label}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Writers</span>
              <span className="text-slate-200">keep editing the working copy</span></div>
          </div>

          {freezeTarget?.blocked_by && (
            <div className="space-y-2">
              <p className="text-xs text-amber-300 flex items-start gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-none" />
                {STAGE_META[freezeTarget.blocked_by]?.label} isn't complete. Opening anyway needs a reason, which is
                recorded on the review.
              </p>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Why is this gate being overridden?"
                className="bg-slate-800 border-slate-600 text-slate-100 text-xs min-h-[60px]" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setFreezeTarget(null); setOverrideReason(''); }}
                    className="border-slate-600 text-slate-200 hover:bg-slate-700">Cancel</Button>
            <Button
              disabled={!!busy || (freezeTarget?.blocked_by && !overrideReason.trim())}
              onClick={() => openReview(freezeTarget, overrideReason.trim() || null)}
              className="bg-slate-100 text-slate-900 hover:bg-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Freeze & open'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
